import { useReducer, useEffect, useCallback } from 'react';
import { listPresets, streamChat, streamSwarm } from '../lib/api';
import type {
  ChatMessage,
  Effort,
  Mode,
  ModelAlias,
  Preset,
  SwarmRun,
  SwarmChannel,
  ToolGroup,
} from '../lib/types';
import { resolveTools } from '../lib/tools';
import { Composer } from '../components/Composer';
import { MessageList } from '../components/MessageList';
import { Header } from '../components/Header';
import { SwarmPanel } from '../components/SwarmPanel';

interface State {
  messages: ChatMessage[];
  streaming: boolean;
  partial: string;
  error: string | null;
  effort: Effort;
  model: ModelAlias;
  mode: Mode;
  judge: boolean;
  toolGroups: ToolGroup[];
  presetIds: string[];
  presets: Preset[];
  account: string | null;
  swarmRun: SwarmRun | null;
}

type Action =
  | { type: 'set_presets'; presets: Preset[] }
  | { type: 'set_effort'; value: Effort }
  | { type: 'set_model'; value: ModelAlias }
  | { type: 'set_mode'; value: Mode }
  | { type: 'set_judge'; value: boolean }
  | { type: 'toggle_tool_group'; id: ToolGroup }
  | { type: 'toggle_preset'; id: string }
  | { type: 'add_user'; text: string }
  | { type: 'start_stream' }
  | { type: 'meta'; account: string }
  | { type: 'delta'; text: string }
  | { type: 'end_stream' }
  | { type: 'error'; message: string }
  | { type: 'reset' }
  | { type: 'swarm_start'; question: string; mode: Mode; judge: boolean }
  | { type: 'swarm_meta'; account: string }
  | { type: 'swarm_role_start'; channel: string; role: string; label: string }
  | { type: 'swarm_text'; channel: string; delta: string }
  | { type: 'swarm_role_end'; channel: string }
  | { type: 'swarm_channel_error'; channel: string; message: string }
  | { type: 'swarm_finish' }
  | { type: 'swarm_error'; message: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set_presets':
      return { ...state, presets: action.presets };
    case 'set_effort':
      return { ...state, effort: action.value };
    case 'set_model':
      return { ...state, model: action.value };
    case 'set_mode':
      return { ...state, mode: action.value };
    case 'set_judge':
      return { ...state, judge: action.value };
    case 'toggle_tool_group': {
      const has = state.toolGroups.includes(action.id);
      return {
        ...state,
        toolGroups: has
          ? state.toolGroups.filter((x) => x !== action.id)
          : [...state.toolGroups, action.id],
      };
    }
    case 'toggle_preset': {
      const has = state.presetIds.includes(action.id);
      return {
        ...state,
        presetIds: has
          ? state.presetIds.filter((x) => x !== action.id)
          : [...state.presetIds, action.id],
      };
    }
    case 'add_user':
      return {
        ...state,
        messages: [...state.messages, { role: 'user', content: action.text }],
        error: null,
      };
    case 'start_stream':
      return { ...state, streaming: true, partial: '', account: null };
    case 'meta':
      return { ...state, account: action.account };
    case 'delta':
      return { ...state, partial: state.partial + action.text };
    case 'end_stream':
      return {
        ...state,
        messages: state.partial
          ? [...state.messages, { role: 'assistant', content: state.partial }]
          : state.messages,
        partial: '',
        streaming: false,
      };
    case 'error':
      return { ...state, error: action.message, streaming: false };
    case 'reset':
      return {
        ...state,
        messages: [],
        partial: '',
        error: null,
        account: null,
        swarmRun: null,
      };
    case 'swarm_start':
      return {
        ...state,
        streaming: true,
        error: null,
        account: null,
        swarmRun: {
          question: action.question,
          mode: action.mode,
          channels: {},
          channelOrder: [],
          judge: action.judge,
          finished: false,
          account: null,
          error: null,
        },
      };
    case 'swarm_meta':
      if (!state.swarmRun) return state;
      return {
        ...state,
        account: action.account,
        swarmRun: { ...state.swarmRun, account: action.account },
      };
    case 'swarm_role_start': {
      if (!state.swarmRun) return state;
      const ch: SwarmChannel = {
        channel: action.channel,
        label: action.label,
        text: '',
        state: 'streaming',
      };
      const order = state.swarmRun.channelOrder.includes(action.channel)
        ? state.swarmRun.channelOrder
        : [...state.swarmRun.channelOrder, action.channel];
      return {
        ...state,
        swarmRun: {
          ...state.swarmRun,
          channels: { ...state.swarmRun.channels, [action.channel]: ch },
          channelOrder: order,
        },
      };
    }
    case 'swarm_text': {
      if (!state.swarmRun) return state;
      const cur = state.swarmRun.channels[action.channel];
      if (!cur) return state;
      return {
        ...state,
        swarmRun: {
          ...state.swarmRun,
          channels: {
            ...state.swarmRun.channels,
            [action.channel]: { ...cur, text: cur.text + action.delta },
          },
        },
      };
    }
    case 'swarm_role_end': {
      if (!state.swarmRun) return state;
      const cur = state.swarmRun.channels[action.channel];
      if (!cur) return state;
      return {
        ...state,
        swarmRun: {
          ...state.swarmRun,
          channels: {
            ...state.swarmRun.channels,
            [action.channel]: { ...cur, state: 'done' },
          },
        },
      };
    }
    case 'swarm_channel_error': {
      if (!state.swarmRun) return state;
      const cur = state.swarmRun.channels[action.channel] ?? {
        channel: action.channel,
        label: action.channel,
        text: '',
        state: 'error' as const,
      };
      const order = state.swarmRun.channelOrder.includes(action.channel)
        ? state.swarmRun.channelOrder
        : [...state.swarmRun.channelOrder, action.channel];
      return {
        ...state,
        swarmRun: {
          ...state.swarmRun,
          channels: {
            ...state.swarmRun.channels,
            [action.channel]: {
              ...cur,
              state: 'error',
              error: action.message,
            },
          },
          channelOrder: order,
        },
      };
    }
    case 'swarm_finish':
      if (!state.swarmRun) return { ...state, streaming: false };
      return {
        ...state,
        streaming: false,
        swarmRun: { ...state.swarmRun, finished: true },
      };
    case 'swarm_error':
      return {
        ...state,
        streaming: false,
        swarmRun: state.swarmRun
          ? { ...state.swarmRun, error: action.message, finished: true }
          : null,
        error: state.swarmRun ? null : action.message,
      };
  }
}

const initialState: State = {
  messages: [],
  streaming: false,
  partial: '',
  error: null,
  effort: 'high',
  model: 'opus',
  mode: 'single',
  judge: false,
  toolGroups: [],
  presetIds: [],
  presets: [],
  account: null,
  swarmRun: null,
};

interface Props {
  onLogout: () => void;
  onOpenCookies?: () => void;
  onOpenHealth?: () => void;
}

export function Chat({ onLogout, onOpenCookies, onOpenHealth }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    listPresets()
      .then((presets) => dispatch({ type: 'set_presets', presets }))
      .catch((e: Error) => console.error('presets:', e.message));
  }, []);

  const sendSingle = useCallback(
    async (text: string, history: ChatMessage[]): Promise<void> => {
      const newMessages: ChatMessage[] = [
        ...history,
        { role: 'user', content: text },
      ];
      dispatch({ type: 'add_user', text });
      dispatch({ type: 'start_stream' });
      const { tools, native_tools } = resolveTools(state.toolGroups);
      try {
        for await (const ev of streamChat({
          messages: newMessages,
          model: state.model,
          effort: state.effort,
          preset_ids: state.presetIds,
          tools: tools.length > 0 ? tools : undefined,
          native_tools: native_tools.length > 0 ? native_tools : undefined,
        })) {
          if (ev.event === 'meta') {
            const data = JSON.parse(ev.data) as { account?: string };
            if (data.account) dispatch({ type: 'meta', account: data.account });
          } else if (ev.event === 'text') {
            const data = JSON.parse(ev.data) as { delta?: string };
            if (data.delta) dispatch({ type: 'delta', text: data.delta });
          } else if (ev.event === 'error') {
            const data = JSON.parse(ev.data) as { message?: string };
            dispatch({ type: 'error', message: data.message ?? 'error' });
            return;
          } else if (ev.event === 'done') {
            break;
          }
        }
        dispatch({ type: 'end_stream' });
      } catch (err) {
        dispatch({ type: 'error', message: (err as Error).message });
      }
    },
    [state.model, state.effort, state.presetIds, state.toolGroups],
  );

  const sendSwarm = useCallback(
    async (text: string, mode: 'perspectives' | 'debate'): Promise<void> => {
      dispatch({ type: 'swarm_start', question: text, mode, judge: state.judge });
      try {
        for await (const ev of streamSwarm({
          messages: [{ role: 'user', content: text }],
          model: state.model,
          effort: state.effort,
          preset_ids: state.presetIds,
          judge: state.judge,
          mode,
        })) {
          if (ev.event === 'meta') {
            const data = JSON.parse(ev.data) as { account?: string };
            if (data.account)
              dispatch({ type: 'swarm_meta', account: data.account });
          } else if (ev.event === 'role_start') {
            const data = JSON.parse(ev.data) as {
              channel: string;
              role: string;
              label: string;
            };
            dispatch({
              type: 'swarm_role_start',
              channel: data.channel,
              role: data.role,
              label: data.label,
            });
          } else if (ev.event === 'text') {
            const data = JSON.parse(ev.data) as {
              channel: string;
              delta: string;
            };
            dispatch({
              type: 'swarm_text',
              channel: data.channel,
              delta: data.delta,
            });
          } else if (ev.event === 'role_end') {
            const data = JSON.parse(ev.data) as { channel: string };
            dispatch({ type: 'swarm_role_end', channel: data.channel });
          } else if (ev.event === 'error') {
            const data = JSON.parse(ev.data) as {
              channel?: string;
              message?: string;
            };
            const msg = data.message ?? 'error';
            if (data.channel && data.channel !== 'system') {
              dispatch({
                type: 'swarm_channel_error',
                channel: data.channel,
                message: msg,
              });
            } else {
              dispatch({ type: 'swarm_error', message: msg });
              return;
            }
          } else if (ev.event === 'done') {
            break;
          }
        }
        dispatch({ type: 'swarm_finish' });
      } catch (err) {
        dispatch({ type: 'swarm_error', message: (err as Error).message });
      }
    },
    [state.model, state.effort, state.presetIds, state.judge],
  );

  const send = useCallback(
    async (text: string): Promise<void> => {
      if (state.streaming) return;
      if (state.mode === 'perspectives' || state.mode === 'debate') {
        await sendSwarm(text, state.mode);
      } else {
        await sendSingle(text, state.messages);
      }
    },
    [state.streaming, state.mode, state.messages, sendSingle, sendSwarm],
  );

  return (
    <div className="min-h-full flex flex-col">
      <Header
        account={state.account}
        model={state.model}
        onClear={() => dispatch({ type: 'reset' })}
        onLogout={onLogout}
        onOpenCookies={onOpenCookies}
        onOpenHealth={onOpenHealth}
      />
      <main className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3 max-w-3xl md:max-w-5xl mx-auto">
          {state.mode === 'single' && (
            <MessageList
              messages={state.messages}
              partial={state.streaming ? state.partial : null}
              error={state.error}
            />
          )}
          {(state.mode === 'perspectives' || state.mode === 'debate') &&
            state.swarmRun && (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <div className="max-w-[85%] sm:max-w-[75%] px-4 py-2 rounded-2xl bg-blue-600 text-white whitespace-pre-wrap break-words text-[15px]">
                    {state.swarmRun.question}
                  </div>
                </div>
                <SwarmPanel run={state.swarmRun} />
              </div>
            )}
          {state.mode === 'perspectives' && !state.swarmRun && (
            <div className="text-center text-gray-500 mt-16 text-sm px-4">
              Режим 3 перспектив (Скептик / Реалист / Оптимист).
              Опционально включи Судью для сводки.
            </div>
          )}
          {state.mode === 'debate' && !state.swarmRun && (
            <div className="text-center text-gray-500 mt-16 text-sm px-4">
              Дебат: Solver R1 → Скептик-фактчек → Solver R3.
              Опционально + Судья.
            </div>
          )}
          {state.error &&
            (state.mode === 'perspectives' || state.mode === 'debate') &&
            !state.swarmRun && (
              <div className="text-red-400 bg-red-950/40 border border-red-900 p-3 rounded-lg text-sm">
                {state.error}
              </div>
            )}
        </div>
      </main>
      <footer className="border-t border-gray-800 p-3 sticky bottom-0 bg-gray-950">
        <Composer
          presets={state.presets}
          presetIds={state.presetIds}
          effort={state.effort}
          model={state.model}
          mode={state.mode}
          judge={state.judge}
          toolGroups={state.toolGroups}
          streaming={state.streaming}
          onTogglePreset={(id) => dispatch({ type: 'toggle_preset', id })}
          onSetEffort={(v) => dispatch({ type: 'set_effort', value: v })}
          onSetModel={(v) => dispatch({ type: 'set_model', value: v })}
          onSetMode={(v) => dispatch({ type: 'set_mode', value: v })}
          onSetJudge={(v) => dispatch({ type: 'set_judge', value: v })}
          onToggleToolGroup={(id) => dispatch({ type: 'toggle_tool_group', id })}
          onSend={(t) => void send(t)}
        />
      </footer>
    </div>
  );
}
